'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:qogita-web-sellers":true});}catch(e){}}();

import * as ModalPrimitive from '@radix-ui/react-dialog'
import { cva } from 'class-variance-authority'
import {
  ComponentProps,
  createContext,
  ReactNode,
  useContext,
  useState,
} from 'react'

import { cn } from '../../utils/cn'
import { buttonStyles } from './buttonStyles'
import { Icon } from './icon'

export const ModalContext = createContext<{
  close: () => void
  modal: boolean | undefined
} | null>(null)
export function useModalContext() {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error(
      'Modal compound components cannot be rendered outside the Modal component',
    )
  }
  return context
}

type ModalRenderProps = {
  close: () => void
}

export function Modal({
  children,
  defaultOpen,
  modal,
  onOpenChange,
  open: controlledOpen,
}: {
  defaultOpen?: boolean
  modal?: boolean
  open?: boolean
  onOpenChange?: (isOpen: boolean) => void
  children: ReactNode | ((props: ModalRenderProps) => ReactNode)
}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined

  const isOpen = isControlled ? controlledOpen : uncontrolledOpen

  const close = () => {
    if (!isControlled) {
      setUncontrolledOpen(false)
    }
    onOpenChange?.(false)
  }

  return (
    <ModalContext.Provider value={{ close, modal }}>
      <ModalPrimitive.Root
        open={isOpen}
        modal={modal}
        onOpenChange={(isOpen) => {
          if (!isControlled) {
            setUncontrolledOpen(isOpen)
          }
          onOpenChange?.(isOpen)
        }}
      >
        {typeof children === 'function' ? children({ close }) : children}
      </ModalPrimitive.Root>
    </ModalContext.Provider>
  )
}

export const ModalTrigger = ModalPrimitive.Trigger

export const ModalPortal = ModalPrimitive.Portal

export const ModalClose = ModalPrimitive.Close

const modalOverlayStyles = cva('fixed inset-0 z-50 grid bg-gray-900/40', {
  variants: {
    size: {
      fullscreen: 'justify-content-stretch items-stretch',
      standard: 'place-items-center p-4 sm:p-6',
    },
  },
})

export function ModalOverlay({
  className,
  size = 'standard',
  ...props
}: ComponentProps<typeof ModalPrimitive.Overlay> & {
  size?: 'fullscreen' | 'standard'
}) {
  return (
    <ModalPrimitive.Overlay
      className={cn(modalOverlayStyles({ size }), className)}
      {...props}
    />
  )
}

const modalContentStyles = cva(
  'font-outfit relative flex max-h-full w-full flex-col overflow-y-auto bg-white shadow-lg',
  {
    variants: {
      size: {
        fullscreen: '',
        standard: 'max-w-[604px] rounded-lg',
      },
    },
  },
)

const horizontalPadding = 'px-6 sm:px-8'

export function ModalContent({
  className,
  children,
  forceMount,
  size = 'standard',
  ...props
}: Omit<ComponentProps<typeof ModalPrimitive.Content>, 'children'> & {
  forceMount?: boolean
  size?: 'fullscreen' | 'standard'
  children: ReactNode | ((props: ModalRenderProps) => ReactNode)
}) {
  const { close, modal } = useModalContext()
  const content = (
    <ModalPrimitive.Content
      className={cn(modalContentStyles({ size }), className)}
      aria-describedby={undefined}
      forceMount={forceMount}
      {...props}
    >
      {typeof children === 'function' ? children({ close }) : children}
    </ModalPrimitive.Content>
  )
  return (
    <ModalPortal>
      {modal === false ? (
        content
      ) : (
        <ModalOverlay size={size}>{content}</ModalOverlay>
      )}
    </ModalPortal>
  )
}

function ModalHeaderClose() {
  return (
    <ModalPrimitive.Close
      className={cn(
        buttonStyles({ variant: 'text' }),
        'ml-auto size-6 self-start',
      )}
    >
      <Icon name="heroicons/solid/x-mark" className="size-6" />
      <span className="sr-only">Close</span>
    </ModalPrimitive.Close>
  )
}

export function ModalHeader({
  className,
  children,
  close = <ModalHeaderClose />,
  ...props
}: ComponentProps<'div'> & {
  close?: ReactNode
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-4 border-b border-gray-200 py-6 sm:py-8',
        horizontalPadding,
        className,
      )}
      {...props}
    >
      {children}
      {close}
    </div>
  )
}

export function ModalBody({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex-1 py-5 font-light text-gray-700 sm:py-8',
        horizontalPadding,
        className,
      )}
      {...props}
    />
  )
}

export function ModalFooter({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-1.5 rounded-b border-gray-200 py-6 sm:flex-row sm:justify-end sm:border-t sm:py-8 md:flex-row md:items-center',
        horizontalPadding,
        className,
      )}
      {...props}
    />
  )
}

export function ModalTitle({
  className,
  ...props
}: ComponentProps<typeof ModalPrimitive.Title>) {
  return (
    <ModalPrimitive.Title
      className={cn('text-lg text-gray-900', className)}
      {...props}
    />
  )
}

export function ModalDescription({
  className,
  ...props
}: ComponentProps<typeof ModalPrimitive.Description>) {
  return (
    <ModalPrimitive.Description
      className={cn('text-gray-700', className)}
      {...props}
    />
  )
}

export function ModalActions({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 sm:ml-auto sm:flex-row sm:justify-end',
        className,
      )}
    >
      {children}
    </div>
  )
}
